import { useCallback } from 'react';
import clone from 'clone';
import { type NavigateFunction } from 'react-router-dom';
import OfficeFormType from './enums/office-form-types';
import type Office from './models/office';
import { maximumGroupPlanPeriodDays, maximumPlanPeriodDays, minimalPlanPeriodDays } from './models/office.constants';
import NotificationSettings from './models/notification-settings';
import RouteConstants from '../../core/routes.constants';
import i18n from '../../i18n';

export const NavigateToEdit = (navigate: NavigateFunction, keys: string[]): ((officeIdToNavigateTo: string) => void) =>
  useCallback(
    (officeIdToNavigateTo: string): void => {
      keys.forEach((key) => {
        window.localStorage.removeItem(key);
      });

      navigate(`/${RouteConstants.PATH_OFFICES}/${officeIdToNavigateTo}`);
    },
    [navigate, keys],
  );

export const NavigateToOverview = (navigate: NavigateFunction, keys: string[]): (() => void) =>
  useCallback((): void => {
    keys.forEach((key) => {
      window.localStorage.removeItem(key);
    });

    navigate(`/${RouteConstants.PATH_OFFICES}`);
  }, [navigate, keys]);

export const UpdateLocalStorageWithOffice = (isEdited: boolean, key: string, officeToUpdate: Office | undefined): void => {
  if (isEdited) {
    window.localStorage.setItem(key, JSON.stringify(officeToUpdate));
  }
};

/**
 * Why the office's group booking window cannot be saved, or undefined when it can: empty, or from the
 * fill-in period up to a year. Mirrors Office.IsValidGroupPlanPeriodDays on the backend.
 */
export const groupPlanPeriodDaysError = (office: Office): string | undefined => {
  if (!office.groupReservationsEnabled || office.groupPlanPeriodDays == null) {
    return undefined;
  }
  const groupDays = Number(office.groupPlanPeriodDays);
  if (!Number.isFinite(groupDays) || !Number.isInteger(groupDays)) {
    return i18n.t('group-plan-period-whole-days-error').toString();
  }
  // An unreadable fill-in period is reported on its own field; the group window is measured against a valid one.
  const planDays = Number(office.planPeriodDays);
  if (Number.isFinite(planDays) && groupDays < planDays) {
    return i18n.t('minimal-group-plan-period-error').replace('[x]', String(office.planPeriodDays));
  }
  if (Number(office.groupPlanPeriodDays) > maximumGroupPlanPeriodDays) {
    return i18n.t('maximum-plan-period-error').replace('[x]', maximumGroupPlanPeriodDays.toString());
  }
  return undefined;
};

export const ApplyValueToOffice = (
  formType: OfficeFormType,
  value: unknown,
  officeToUpdate: Office,
): { office: Office; errors: Map<OfficeFormType, boolean | string> } => {
  const errors: Map<OfficeFormType, boolean | string> = new Map<OfficeFormType, boolean | string>();
  const officeCopy = { ...clone(officeToUpdate) };

  switch (formType) {
    case OfficeFormType.name:
      officeCopy.name = value as string;
      break;
    case OfficeFormType.company:
      officeCopy.companyId = value as string;
      break;
    case OfficeFormType.maxCapacity:
      if (value === undefined || Number(value) > 100 || Number(value) < 1 || !Number.isInteger(Number(value))) {
        errors.set(OfficeFormType.maxCapacity, true);
        break;
      }
      officeCopy.preferredCapacityPercentage = value as number;
      break;
    case OfficeFormType.isMaxCapacityTheLimit:
      officeCopy.isMaxCapacityTheLimit = value as boolean;
      break;
    case OfficeFormType.floorPlanEnabled:
      if (!value) {
        officeCopy.groupReservationsEnabled = false;
        officeCopy.maximumGroupSize = undefined;
        officeCopy.maximumGroupLeaders = undefined;
        officeCopy.groupPlanPeriodDays = undefined;
      }
      officeCopy.floorPlanEnabled = value as boolean;
      break;
    case OfficeFormType.lunchEnabled:
      officeCopy.lunchEnabled = value as boolean;
      break;
    case OfficeFormType.qrCodeEnabled:
      if (value === false) {
        officeCopy.qrCodesEnabled = false;
        officeCopy.floorPlanEnabled = false;
        officeCopy.groupReservationsEnabled = false;
        officeCopy.maximumGroupSize = undefined;
        officeCopy.maximumGroupLeaders = undefined;
        officeCopy.groupPlanPeriodDays = undefined;
      }
      officeCopy.qrCodesEnabled = value as boolean;
      break;
    case OfficeFormType.isFloorPlanAvailable:
      if (value === false) {
        officeCopy.floorPlanEnabled = false;
        officeCopy.groupReservationsEnabled = false;
        officeCopy.maximumGroupSize = undefined;
        officeCopy.maximumGroupLeaders = undefined;
        officeCopy.groupPlanPeriodDays = undefined;
      }
      officeCopy.isFloorPlanAvailable = value as boolean;
      break;
    case OfficeFormType.webLabel:
      officeCopy.webLabel = value as string;
      break;
    case OfficeFormType.webUrl:
      officeCopy.webUrl = value as string;
      break;
    case OfficeFormType.planPeriodDays: {
      const planPeriodDays = value as number;
      if (Number(planPeriodDays) < minimalPlanPeriodDays) {
        errors.set(OfficeFormType.planPeriodDays, i18n.t('minimal-plan-period-error').replace('[x]', minimalPlanPeriodDays.toString()));
        break;
      }
      if (Number(planPeriodDays) > maximumPlanPeriodDays) {
        errors.set(OfficeFormType.planPeriodDays, i18n.t('maximum-plan-period-error').replace('[x]', maximumPlanPeriodDays.toString()));
        break;
      }
      // The group window may extend the fill-in period but never be shorter than it; the API refuses
      // the pair otherwise. Raising the fill-in period past it asks for the group window first rather
      // than silently changing a number the administrator set.
      // Only against a group window that is itself valid and in use: a half-typed or hidden one must not
      // block the fill-in period with a message that does not describe the problem.
      if (
        officeCopy.groupReservationsEnabled &&
        officeCopy.groupPlanPeriodDays != null &&
        groupPlanPeriodDaysError(officeCopy) == null &&
        Number(planPeriodDays) > Number(officeCopy.groupPlanPeriodDays)
      ) {
        errors.set(OfficeFormType.planPeriodDays, i18n.t('plan-period-exceeds-group-window-error').toString());
        break;
      }
      officeCopy.planPeriodDays = planPeriodDays;
      break;
    }
    case OfficeFormType.bookingTimeZone:
      officeCopy.bookingTimeZone = value as string;
      break;
    // case OfficeFormType.addPermittedUser:
    //   officeCopy.permittedUsers.push(value);
    //   break;
    // case OfficeFormType.addPermittedUserName:
    //   officeCopy.permittedUsers = officeCopy.permittedUsers.filter((item) => item.email !== value.email);
    //   officeCopy.permittedUsers.push(value);
    //   break;
    // case OfficeFormType.updatePermittedUser:
    //   officeCopy.permittedUsers = officeCopy.permittedUsers.filter((item) => item.id !== value.id);
    //   officeCopy.permittedUsers.push(value);
    //   break;
    // case OfficeFormType.deleteEmail:
    //   officeCopy.permittedUsers = officeCopy.permittedUsers.filter((item) => item.email !== value.email);
    //   break;
    // case OfficeFormType.addPermittedAdmin:
    //   officeCopy.permittedAdmins.push(value);
    //   break;
    // case OfficeFormType.deletePermittedAdmin:
    //   officeCopy.permittedAdmins = officeCopy.permittedAdmins.filter((item) => item.email !== value.email);
    //   break;
    // case OfficeFormType.addRoom:
    //   value.id = uuidv4();
    //   officeCopy.rooms.push(value);
    //   break;
    // case OfficeFormType.deleteRoom:
    //   officeCopy.rooms = officeCopy.rooms.filter((item: { name: any }) => item.name !== value.name);
    //   break;
    // case OfficeFormType.updateRoom:
    //   officeCopy.rooms = officeCopy.rooms.filter((item) => item.id !== value.id);
    //   officeCopy.rooms.push(value);
    //   break;
    // case OfficeFormType.addDepartment:
    //   value.department.id = uuidv4();
    //   officeCopy.departments = officeCopy.departments.filter((item: { name: any }) => item.name !== value.department.name);
    //   officeCopy.departments.push(value.department);
    //   officeCopy.rooms = value.rooms;
    //   break;
    // case OfficeFormType.deleteDepartment:
    //   officeCopy.rooms = value.rooms;
    //   officeCopy.departments = officeCopy.departments.filter((item) => item.id !== value.department.id);
    //   break;
    // case OfficeFormType.updateDepartment:
    //   officeCopy.departments = officeCopy.departments.filter((item) => item.id !== value.department.id);
    //   officeCopy.departments.push(value.department);
    //   officeCopy.rooms = value.rooms;
    //   break;
    case OfficeFormType.notificationDay:
      if (officeCopy.notificationSettings == null) {
        officeCopy.notificationSettings = new NotificationSettings();
      }
      officeCopy.notificationSettings.day = value as number;
      break;
    case OfficeFormType.notificationTime:
      if (officeCopy.notificationSettings == null) {
        officeCopy.notificationSettings = new NotificationSettings();
      }
      officeCopy.notificationSettings.hour = (value as Date).getHours();
      officeCopy.notificationSettings.minute = (value as Date).getMinutes();
      break;
    case OfficeFormType.groupReservationsEnabled:
      if (!value) {
        officeCopy.maximumGroupSize = undefined;
        officeCopy.maximumGroupLeaders = undefined;
        officeCopy.groupPlanPeriodDays = undefined;
      }
      officeCopy.groupReservationsEnabled = value as boolean;
      break;
    case OfficeFormType.maximumGroupSize: {
      const maximumGroupSize = value as number;

      if (maximumGroupSize === undefined) {
        officeCopy.maximumGroupSize = undefined;
        break;
      }
      if (!Number.isInteger(Number(maximumGroupSize) || Number(maximumGroupSize) === 0)) {
        break;
      }

      if (Number(maximumGroupSize) < 2) {
        errors.set(OfficeFormType.maximumGroupSize, i18n.t('maximum-group-size-error').toString());
        break;
      }
      // The same pairing the leader field checks, from this side. Without it, lowering the group size
      // under an already-saved leader limit slips through and leaves the office with a limit that can
      // never be reached.
      if (officeCopy.maximumGroupLeaders != null && Number(maximumGroupSize) < Number(officeCopy.maximumGroupLeaders)) {
        errors.set(OfficeFormType.maximumGroupSize, i18n.t('maximum-group-leaders-exceeds-size-error').toString());
        break;
      }
      officeCopy.maximumGroupSize = maximumGroupSize;
      break;
    }
    case OfficeFormType.maximumGroupLeaders: {
      const maximumGroupLeaders = value as number;

      // Emptying the field clears the setting rather than storing 0, which would otherwise be read
      // as "no leaders allowed". The API puts a cleared office back on its own default.
      if (maximumGroupLeaders === undefined || Number.isNaN(Number(maximumGroupLeaders)) || Number(maximumGroupLeaders) === 0) {
        officeCopy.maximumGroupLeaders = undefined;
        officeCopy.groupPlanPeriodDays = undefined;
        break;
      }
      if (!Number.isInteger(Number(maximumGroupLeaders))) {
        break;
      }
      // At least one: a group with no leader cannot be edited by anybody in it.
      if (Number(maximumGroupLeaders) < 1) {
        errors.set(OfficeFormType.maximumGroupLeaders, i18n.t('maximum-group-leaders-error').toString());
        break;
      }
      // And never more leaders than the group is allowed members, which would be a limit that can
      // never be reached.
      if (officeCopy.maximumGroupSize != null && Number(maximumGroupLeaders) > Number(officeCopy.maximumGroupSize)) {
        errors.set(OfficeFormType.maximumGroupLeaders, i18n.t('maximum-group-leaders-exceeds-size-error').toString());
        break;
      }
      officeCopy.maximumGroupLeaders = maximumGroupLeaders;
      break;
    }
    case OfficeFormType.groupPlanPeriodDays: {
      const groupPlanPeriodDays = value as number | undefined;

      // Emptying the field clears the setting: group bookings then use the fill-in period.
      if (groupPlanPeriodDays === undefined || Number.isNaN(Number(groupPlanPeriodDays)) || Number(groupPlanPeriodDays) === 0) {
        officeCopy.groupPlanPeriodDays = undefined;
        break;
      }
      // Kept even when out of range or not a whole number, with the error shown under the field (see the
      // end of this function). Rejecting it instead would snap the input back on every keystroke: typing
      // "42" passes through "4", below a 14-day fill-in period, so a two-digit window could never be
      // entered. Save refuses while it is invalid.
      officeCopy.groupPlanPeriodDays = groupPlanPeriodDays;
      break;
    }
    case OfficeFormType.isAvailableInApp:
      officeCopy.isAvailableInApp = value as boolean;
      break;
    case OfficeFormType.cateringNotificationEnabled:
      officeCopy.cateringNotificationEnabled = value as boolean;
      break;
    case OfficeFormType.cateringNotificationEmailSubject:
      officeCopy.cateringNotificationEmailSubject = value as string;
      break;
    case OfficeFormType.cateringNotificationEmailBody:
      officeCopy.cateringNotificationEmailBody = value as string;
      break;
    case OfficeFormType.cateringNotificationRecipients:
      officeCopy.cateringNotificationRecipients = value as string;
      break;
    case OfficeFormType.cateringNotificationSendTime:
      officeCopy.cateringNotificationSendTime = value as string;
      break;
    case OfficeFormType.cateringNotificationTimeZone:
      officeCopy.cateringNotificationTimeZone = value as string;
      break;
    case OfficeFormType.cateringNotificationDaysAhead:
      officeCopy.cateringNotificationDaysAhead = value as number | undefined;
      break;
    case OfficeFormType.officeThresholdMinPercentage:
      officeCopy.thresholdMinPercentage = value as number | undefined;
      break;
    case OfficeFormType.officeThresholdMaxPercentage:
      officeCopy.thresholdMaxPercentage = value as number | undefined;
      break;
    default:
      break;
  }

  // The group window is re-checked on every change, not only when it is edited: the error map is rebuilt
  // per change, so an invalid window would otherwise lose its message the moment another field is touched.
  const groupWindowError = groupPlanPeriodDaysError(officeCopy);
  if (groupWindowError != null && !errors.has(OfficeFormType.groupPlanPeriodDays)) {
    errors.set(OfficeFormType.groupPlanPeriodDays, groupWindowError);
  }

  return { office: officeCopy, errors };
};
